import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import {
  edgeGroupAdmissionSchema,
  edgeGroupApprovalBeginRequestSchema,
  edgeGroupApprovalBeginResponseSchema,
  edgeGroupApprovalFinishRequestSchema,
  edgeGroupApprovalPolicySchema,
  edgeGroupApprovalResultSchema,
  edgeGroupChangeEvidenceSchema,
  edgeGroupChangeRequestSchema,
  edgeGroupChangeSchema,
  edgeGroupChangesSchema,
  edgeGroupDecommissionedSchema,
  edgeGroupDoorAuthoritySchema,
  edgeGroupDraftRequestSchema,
  edgeGroupJoinTokenIssuedSchema,
  edgeGroupJoinTokenRequestSchema,
  edgeGroupNamesRequestSchema,
  edgeGroupJoinTokenSchema,
  edgeGroupJoinTokensSchema,
  edgeGroupPendingSchema,
  edgeGroupReleasesSchema,
  edgeGroupRolloutSchema,
  edgeGroupUpdateRequestSchema,
  edgeGroupRetirementReceiptSchema,
  edgeGroupRemovalRequestSchema,
  edgeGroupRemovalResponseSchema,
  edgeGroupSchema,
  edgeGroupsSchema,
  edgeGroupWithdrawalRequestSchema,
  edgeGroupWithdrawalResponseSchema,
} from "@/generated/edge-approval-contract";
import { apiFetch } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { withStepUp } from "@/lib/with-step-up";
import type { ApprovalSubject, ApprovalTransport } from "./approval";
import { groupPollInterval, type ApprovalPolicyRequest, type ChangeRequest, type DraftRequest, type EdgeGroup, type JoinTokenRequest, type NamesRequest, type RemovalRequest, type RolloutAction, type UpdateRequest, type WithdrawalRequest } from "./schemas";

const base = (tenant: string) => `/api/v1/tenants/${encodeURIComponent(tenant)}/edge-groups`;
const groupURL = (tenant: string, group: string) => `${base(tenant)}/${encodeURIComponent(group)}`;

export const fetchEdgeGroups = async (tenant: string) => edgeGroupsSchema.parse(await apiFetch<unknown>(base(tenant)));
/** A read-only download; preserve the validated signed document without caching its projection. */
export function useDownloadRetirementReceipt(tenant: string, group: string, edge: string) {
  return useMutation({ mutationFn: async () => {
    const raw = await apiFetch<unknown>(`${groupURL(tenant, group)}/edges/${encodeURIComponent(edge)}/retirement`);
    const receipt = edgeGroupRetirementReceiptSchema.parse(raw);
    if (receipt.retirement.tenant_id !== tenant || receipt.retirement.group_id !== group || receipt.retirement.edge_id !== edge) {
      throw new Error("The receipt does not name this Edge. Reload the group before trying again.");
    }
    const url = URL.createObjectURL(new Blob([JSON.stringify(raw, null, 2)], { type: "application/json" }));
    try {
      const link = document.createElement("a");
      link.href = url;
      link.download = `edge-${edge}-retirement.json`;
      link.click();
    } finally {
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  } });
}
export const fetchEdgeGroup = async (tenant: string, group: string) => edgeGroupSchema.parse(await apiFetch<unknown>(groupURL(tenant, group)));

/** Replace one group wherever it is cached and refresh the navigation count. */
function storeGroup(client: QueryClient, tenant: string, group: EdgeGroup) {
  client.setQueryData(queryKeys.edgeGroups.group(tenant, group.id), group);
  void client.invalidateQueries({ queryKey: queryKeys.edgeGroups.changes(tenant, group.id) });
  client.setQueryData<{ groups: EdgeGroup[] }>(queryKeys.edgeGroups.list(tenant), (prior) => prior ? { groups: prior.groups.map((item) => item.id === group.id ? group : item) } : prior);
  void client.invalidateQueries({ queryKey: queryKeys.edgeGroups.pending(tenant) });
}

function refreshGroup(client: QueryClient, tenant: string, group: string) {
  // The group's key prefixes its releases, join tokens and change history, which refresh with it.
  void client.invalidateQueries({ queryKey: queryKeys.edgeGroups.group(tenant, group) });
  void client.invalidateQueries({ queryKey: queryKeys.edgeGroups.list(tenant) });
  void client.invalidateQueries({ queryKey: queryKeys.edgeGroups.pending(tenant) });
}

// ── Reads ─────────────────────────────────────────────────────────────────────
/** The groups list is read on entry and after writes; the open group carries the polling. */
export function useEdgeGroups(tenant: string) {
  return useQuery({
    queryKey: queryKeys.edgeGroups.list(tenant),
    queryFn: () => fetchEdgeGroups(tenant),
    enabled: tenant.length > 0,
  });
}

/** The Edge door root agents pin. It changes only when the deployment rotates it. */
export function useEdgeDoorAuthority(tenant: string) {
  return useQuery({
    queryKey: queryKeys.edgeGroups.doorAuthority(tenant),
    queryFn: async () => edgeGroupDoorAuthoritySchema.parse(await apiFetch<unknown>(`${base(tenant)}/door-authority`)),
    enabled: tenant.length > 0,
    staleTime: Infinity,
  });
}

/** The tenant's separation of duties for approvals. It changes only when an administrator sets it. */
export function useEdgeApprovalPolicy(tenant: string) {
  return useQuery({
    queryKey: queryKeys.edgeGroups.approvalPolicy(tenant),
    queryFn: async () => edgeGroupApprovalPolicySchema.parse(await apiFetch<unknown>(`${base(tenant)}/approval-policy`)),
    enabled: tenant.length > 0,
  });
}

/** One group, polled every 10s only while something on it is pending or moving (groupPollInterval). */
export function useEdgeGroup(tenant: string, group: string | undefined) {
  const client = useQueryClient();
  return useQuery({
    queryKey: queryKeys.edgeGroups.group(tenant, group ?? ""),
    queryFn: () => fetchEdgeGroup(tenant, group ?? ""),
    enabled: tenant.length > 0 && !!group,
    initialData: () => client.getQueryData<{ groups: EdgeGroup[] }>(queryKeys.edgeGroups.list(tenant))?.groups.find((item) => item.id === group),
    initialDataUpdatedAt: () => client.getQueryState(queryKeys.edgeGroups.list(tenant))?.dataUpdatedAt,
    refetchInterval: (query) => groupPollInterval(query.state.data),
  });
}

/** Items awaiting an approval the viewer may give, for the navigation badge. Same 60s cadence as other badges. */
export function useEdgeGroupsPending(tenant: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.edgeGroups.pending(tenant ?? ""),
    queryFn: async () => edgeGroupPendingSchema.parse(await apiFetch<unknown>(`${base(tenant ?? "")}/pending`)),
    enabled: enabled && !!tenant,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

export function useGroupReleases(tenant: string, group: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.edgeGroups.releases(tenant, group),
    queryFn: async () => edgeGroupReleasesSchema.parse(await apiFetch<unknown>(`${groupURL(tenant, group)}/releases`)),
    enabled,
  });
}

/** The group's change history. Fetched when shown and after a change moves; changes are not polled. */
export function useChangeHistory(tenant: string, group: string) {
  return useQuery({
    queryKey: queryKeys.edgeGroups.changes(tenant, group),
    queryFn: async () => edgeGroupChangesSchema.parse(await apiFetch<unknown>(`${groupURL(tenant, group)}/changes`)),
  });
}

/** One change's evidence record, saved as a JSON file. The record is never cached. */
export function useDownloadChangeEvidence(tenant: string, group: string) {
  return useMutation({
    mutationFn: async (change: string) => {
      const raw = await apiFetch<unknown>(`${groupURL(tenant, group)}/changes/${encodeURIComponent(change)}/evidence`);
      const evidence = edgeGroupChangeEvidenceSchema.parse(raw);
      const url = URL.createObjectURL(new Blob([JSON.stringify(raw, null, 2)], { type: "application/json" }));
      try {
        const link = document.createElement("a");
        link.href = url;
        link.download = `edge-group-change-${evidence.summary.id}.json`;
        link.click();
      } finally {
        URL.revokeObjectURL(url);
      }
      return evidence.summary.id;
    },
  });
}

export function useJoinTokens(tenant: string, group: string) {
  return useQuery({
    queryKey: queryKeys.edgeGroups.joinTokens(tenant, group),
    queryFn: async () => edgeGroupJoinTokensSchema.parse(await apiFetch<unknown>(`${groupURL(tenant, group)}/join-tokens`)),
  });
}

// ── Drafting and changes (interactive admin; no step-up — the passkey belongs to approval) ──
export function useCreateGroup(tenant: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: { id: string; name: string }) => edgeGroupSchema.parse(await apiFetch<unknown>(base(tenant), { method: "POST", body })),
    onSuccess: (group) => {
      client.setQueryData(queryKeys.edgeGroups.group(tenant, group.id), group);
      void client.invalidateQueries({ queryKey: queryKeys.edgeGroups.list(tenant) });
    },
    retry: false,
  });
}

/** Records how the group's name resolves: the forwarder, or the static records shown. */
export function useIssueNames(tenant: string, group: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (request: NamesRequest) => edgeGroupSchema.parse(await apiFetch<unknown>(`${groupURL(tenant, group)}/names`, { method: "POST", body: edgeGroupNamesRequestSchema.parse(request) })),
    onSuccess: (saved) => storeGroup(client, tenant, saved),
    retry: false,
  });
}

export function useSaveDraft(tenant: string, group: string) {
  const client = useQueryClient();
  return useMutation({
    // The caller keeps request_id through an uncertain outcome so a retry is answered once.
    mutationFn: async (request: DraftRequest) => edgeGroupSchema.parse(await apiFetch<unknown>(`${groupURL(tenant, group)}/draft`, { method: "PUT", body: edgeGroupDraftRequestSchema.parse(request) })),
    onSuccess: (saved) => storeGroup(client, tenant, saved),
    retry: false,
  });
}

export function useReloadGroup(tenant: string, group: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => fetchEdgeGroup(tenant, group),
    onSuccess: (current) => storeGroup(client, tenant, current),
    retry: false,
  });
}

export function usePrepareChange(tenant: string, group: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (request: ChangeRequest) => edgeGroupChangeSchema.parse(await apiFetch<unknown>(`${groupURL(tenant, group)}/changes`, { method: "POST", body: edgeGroupChangeRequestSchema.parse(request) })),
    onSuccess: () => refreshGroup(client, tenant, group),
    retry: false,
  });
}

/** Prepare a release update or rollback; it waits for the same one-passkey approval as any change. */
export function usePrepareUpdate(tenant: string, group: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (request: UpdateRequest) => edgeGroupChangeSchema.parse(await apiFetch<unknown>(`${groupURL(tenant, group)}/updates`, { method: "POST", body: edgeGroupUpdateRequestSchema.parse(request) })),
    onSuccess: () => refreshGroup(client, tenant, group),
    retry: false,
  });
}

/** Pause, resume or cancel the group's rollout. Each only holds or ends an approved update. */
export function useRolloutAction(tenant: string, group: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (action: RolloutAction) => edgeGroupRolloutSchema.parse(await apiFetch<unknown>(`${groupURL(tenant, group)}/rollout/${action}`, { method: "POST" })),
    onSuccess: () => refreshGroup(client, tenant, group),
    retry: false,
  });
}

export function useCancelChange(tenant: string, group: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (change: string) => edgeGroupChangeSchema.parse(await apiFetch<unknown>(`${groupURL(tenant, group)}/changes/${encodeURIComponent(change)}/cancel`, { method: "POST" })),
    onSuccess: () => refreshGroup(client, tenant, group),
    retry: false,
  });
}

export function useRejectAdmission(tenant: string, group: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (admission: string) => edgeGroupAdmissionSchema.parse(await apiFetch<unknown>(`${groupURL(tenant, group)}/admissions/${encodeURIComponent(admission)}/reject`, { method: "POST" })),
    onSuccess: () => refreshGroup(client, tenant, group),
    retry: false,
  });
}

// ── Approval ──────────────────────────────────────────────────────────────────
/**
 * The approval ceremony's HTTP legs. Begin records a durable approval over the exact items; finish
 * verifies the assertion and the server applies everything in one transaction. A repeated finish
 * with the same assertion is answered with the same result.
 */
export function approvalTransport(tenant: string, group: string): ApprovalTransport {
  const url = `${groupURL(tenant, group)}/approvals`;
  return {
    begin: async (subject: ApprovalSubject) => {
      const begun = edgeGroupApprovalBeginResponseSchema.parse(await apiFetch<unknown>(url, {
        method: "POST",
        body: edgeGroupApprovalBeginRequestSchema.parse({ change_id: subject.change?.id ?? null, admissions: subject.admissions.map((a) => a.id) }),
      }));
      const options = begun.options;
      return {
        approvalId: begun.approval_id,
        options: {
          ...options,
          allowCredentials: options.allowCredentials?.map((credential) => ({
            ...credential,
            transports: credential.transports?.filter((transport) => transport !== "smart-card"),
          })),
        },
      };
    },
    finish: async (approvalId, assertion) => edgeGroupApprovalResultSchema.parse(await apiFetch<unknown>(`${url}/${encodeURIComponent(approvalId)}`, {
      method: "POST",
      body: edgeGroupApprovalFinishRequestSchema.parse({ assertion }),
    })),
  };
}

/** After an approval the server has applied everything; refresh what the page shows. */
export function useApprovalApplied(tenant: string, group: string) {
  const client = useQueryClient();
  return () => refreshGroup(client, tenant, group);
}

// ── Step-up acts ──────────────────────────────────────────────────────────────
/**
 * Issue a join token. The secret is shown once: it lives only in this mutation's resolved value,
 * never in a query cache. The token list is refetched, which never carries a secret.
 */
export function useIssueJoinToken(tenant: string, group: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (request: JoinTokenRequest) => withStepUp(async () => edgeGroupJoinTokenIssuedSchema.parse(await apiFetch<unknown>(`${groupURL(tenant, group)}/join-tokens`, { method: "POST", body: edgeGroupJoinTokenRequestSchema.parse(request) }))),
    onSuccess: () => void client.invalidateQueries({ queryKey: queryKeys.edgeGroups.joinTokens(tenant, group) }),
    gcTime: 0,
    retry: false,
  });
}

export function useRevokeJoinToken(tenant: string, group: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (token: string) => withStepUp(async () => edgeGroupJoinTokenSchema.parse(await apiFetch<unknown>(`${groupURL(tenant, group)}/join-tokens/${encodeURIComponent(token)}/revoke`, { method: "POST" }))),
    onSuccess: () => void client.invalidateQueries({ queryKey: queryKeys.edgeGroups.joinTokens(tenant, group) }),
    retry: false,
  });
}

/** Change who must approve: it loosens or tightens separation of duties, so it takes a fresh step-up. */
export function useSetEdgeApprovalPolicy(tenant: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (request: ApprovalPolicyRequest) => withStepUp(async () => edgeGroupApprovalPolicySchema.parse(await apiFetch<unknown>(`${base(tenant)}/approval-policy`, { method: "PUT", body: request }))),
    onSuccess: (policy) => {
      client.setQueryData(queryKeys.edgeGroups.approvalPolicy(tenant), policy);
      void client.invalidateQueries({ queryKey: ["edge-groups", tenant] });
    },
    retry: false,
  });
}

/** Decommission a group: every Edge drains and the group retires; the act takes a fresh step-up. */
export function useDecommissionGroup(tenant: string, group: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ confirmName, drainSeconds }: { confirmName: string; drainSeconds: number }) => withStepUp(async () => edgeGroupDecommissionedSchema.parse(await apiFetch<unknown>(`${groupURL(tenant, group)}/decommission`, { method: "POST", body: { confirm_name: confirmName, drain_seconds: drainSeconds } }))),
    onSuccess: (result) => {
      if (result.retired_at) client.removeQueries({ queryKey: queryKeys.edgeGroups.group(tenant, group) });
      refreshGroup(client, tenant, group);
    },
    retry: false,
  });
}

/** Remove an Edge with a drain. It reduces authority, so it takes a fresh step-up. */
export function useRemoveEdge(tenant: string, group: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ edge, request }: { edge: string; request: RemovalRequest }) => withStepUp(async () => edgeGroupRemovalResponseSchema.parse(await apiFetch<unknown>(`${groupURL(tenant, group)}/edges/${encodeURIComponent(edge)}/removal`, { method: "POST", body: edgeGroupRemovalRequestSchema.parse(request) }))),
    onSuccess: (result) => storeGroup(client, tenant, result.group),
    retry: false,
  });
}

/** Emergency withdrawal: irreversible, overrides the minimum, and takes a fresh step-up. */
export function useWithdrawEdge(tenant: string, group: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (request: WithdrawalRequest) => withStepUp(async () => edgeGroupWithdrawalResponseSchema.parse(await apiFetch<unknown>(`${groupURL(tenant, group)}/withdrawals`, { method: "POST", body: edgeGroupWithdrawalRequestSchema.parse(request) }))),
    onSuccess: (result) => storeGroup(client, tenant, result.group),
    retry: false,
  });
}

